import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { colors } from '@fs/zion-ui'
import { AuthRoute, NavLink, NotFound, Redirect, Route, Switch, withRouter } from '@fs/zion-router'
import { PermissionsContext } from './PermissionsContext'
import { marginBottom } from '../../util/CssUtil'
import NotImplemented from '../notimplemented/NotImplemented'

const subsubnav = css`
  display: flex;
  flex-wrap: wrap;
  position: relative;

  margin-bottom: 10px;
  margin-top: -10px;
  &::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    inset-inline-end: 0;
    bottom: -3px;
  }
  a,
  a:visited {
    color: ${colors.gray60};
    padding: 4px 10px;
    border-radius: 4px;
    margin: 2px;
    transition: background 0.3s;
    font-size: smaller;
    text-decoration: none;
  }
  a:first-of-type {
    margin-inline-start: 0;
  }
  a:hover {
    background: #f1f8e3;
    text-decoration: none;
  }
  a:active,
  a[aria-current] {
    text-decoration: none;
    background: ${colors.green03};
  }
`
const bottomLineCss = css`
  border-bottom: 1px solid ${colors.gray80};
`

const mb20 = marginBottom('20px')

export const SubSubNav = ({ children, bottomLine, ...props }) => {
  const cssClasses = [subsubnav]
  bottomLine && cssClasses.push(bottomLineCss)
  return (
    <div css={mb20}>
      <nav css={cssClasses} {...props}>
        {children}
      </nav>
    </div>
  )
}

const getSeparator = (path) => (path.indexOf('?') > -1 ? '&' : '?')

const buildSubnavAndRoutes = ({ data, match, forward }) => {
  const subNavElements = []
  const routingElements = []

  data.forEach((item) => {
    if (item.text) {
      const separator = getSeparator(item.path)
      if (item.nonParentUrl) {
        subNavElements.push(
          <NavLink key={item.path} to={`/${item.path}`}>
            {item.text}
          </NavLink>
        )
      } else {
        let forwardParams = ''
        if (forward !== null && forward !== undefined) {
          forwardParams =
            match.url.includes('person') || match.url.includes('user') ? `${separator}forward=${forward}` : ''
        }
        subNavElements.push(
          <NavLink
            key={item.path}
            to={`${match.url}/${item.path}${forwardParams}`}
            isActive={(isActiveMatch) => isActiveMatch}
          >
            {item.text}
          </NavLink>
        )
      }
    }
    routingElements.push(
      <AuthRoute
        key={item.path}
        exact={item.exact}
        path={`${match.path}/${item.path.split('?')[0]}`}
        component={item.component ? item.component : NotImplemented}
      />
    )
  })

  return { subNavElements, routingElements }
}
function SubnavAndRoutes({ data, match, defaultPath, forward, staticContext: _, bottomLine, ...props }) {
  const { isAuthorized, isRole } = useContext(PermissionsContext)
  const filteredData = data.filter(
    (item) =>
      ((!item.requiredRoles && isAuthorized(item.requiredPermissions)) || isRole(item.requiredRoles)) &&
      (!item.featureFlag || item.featureFlag.isOn)
  )
  const { subNavElements, routingElements } = buildSubnavAndRoutes({
    data: filteredData,
    match,
    forward,
  })

  // if defaultPath is not available for the logged-in user because he/she is lacking
  // the required role or permission, and he/she just clicks on the upper tab, we redirect to the first available tab
  const defaultPathValue = filteredData.find((item) => item.path === defaultPath)?.path || filteredData[0]?.path
  const redirectPath = defaultPathValue ? defaultPathValue.split(':')[0].replace(/\/$/, '') : defaultPathValue

  return (
    <>
      <SubSubNav bottomLine={bottomLine} {...props}>
        {subNavElements}
      </SubSubNav>
      <Switch>
        {redirectPath && <Redirect from={`${match.url}/`} exact to={`${match.path}/${redirectPath}`} />}
        {routingElements}
        <Route component={NotFound} />
      </Switch>
    </>
  )
}

SubnavAndRoutes.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
      path: PropTypes.string,
      translationKey: PropTypes.string,
      requiredPermissions: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    })
  ),
  forward: PropTypes.bool,
  bottomLine: PropTypes.bool,
}

SubnavAndRoutes.defaultProps = {
  bottomLine: true,
}

export default withRouter(SubnavAndRoutes)
